// Einkauf, Pfand und Finanzen: Einkaufsvolumen und Liefertreue je Lieferant, Preisentwicklung je Artikel, Leergut im
// Umlauf und Pfandverbindlichkeiten, Altersstruktur der Forderungen und Umsatz gegen Zahlungseingang.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { AGE_BUCKETS } from '../lib/analysis'
import { TIME_BUCKETS } from '../lib/period'
import { compareField, csvField, locationFields, periodFields, READ_FINANCE, READ_PURCHASING, reportScope, zCsv, zDay, zPeriod } from './common'

// ---- Einkauf ------------------------------------------------------------------------------------------------------------

export const purchasingReport = defineContract({
  name: 'reporting.purchasing.report',
  title: 'Einkauf auswerten',
  description: 'Einkaufsvolumen je Lieferant mit Liefertreue: pünktlich, mit Fehlmenge, mit beschädigter Ware.',
  llm: {
    description:
      'Wertet die gebuchten Wareneingänge eines Zeitraums je Lieferant aus: Zahl der Lieferungen, Warenwert in Cent netto (Einkaufspreise – nur mit dem Recht auf Einstandspreise lesbar, deshalb verlangt die ganze Auswertung dieses Recht), ' +
      'Liefertreue (pünktlich gegenüber dem Liefertermin der Bestellung, Lieferungen mit Fehlmenge, mit beschädigter Ware) und offenes Bestellvolumen. ' +
      'Verwenden für „Bei wem kaufen wir am meisten?“, „Welcher Lieferant liefert unzuverlässig?“. Nicht für einzelne Bestellungen (purchasing.order.list) oder Preise eines Artikels (reporting.purchasing.prices).',
    keywords: ['Einkaufsvolumen', 'Lieferanten', 'Liefertreue', 'Fehlmengen', 'pünktlich', 'beschädigt'],
    examples: [{ period: 'last30' }],
  },
  input: z.strictObject({ ...periodFields, ...compareField, ...locationFields, limit: z.int().min(1).max(500).default(100), ...csvField }),
  output: z.object({
    period: zPeriod,
    comparison: zPeriod.nullable(),
    comparisonLabel: z.string().nullable(),
    totals: z.object({
      receiptCount: z.int(),
      receivedCents: z.int(),
      comparisonReceivedCents: z.int().nullable(),
      changeBp: z.int().nullable(),
      /** bestellt, noch nicht (vollständig) geliefert */
      openOrderCount: z.int(),
      openOrderCents: z.int(),
      /** Lieferungen mit Liefertermin: Anteil pünktlich in Basispunkten; null ohne solche Lieferungen */
      onTimeBp: z.int().nullable(),
      shortCount: z.int(),
      damagedCount: z.int(),
    }),
    rows: z.array(
      z.object({
        supplierId: zId,
        number: z.string().nullable(),
        name: z.string(),
        receiptCount: z.int(),
        receivedCents: z.int(),
        shareBp: z.int(),
        comparisonReceivedCents: z.int().nullable(),
        /** Lieferungen mit Liefertermin der Bestellung */
        datedCount: z.int(),
        onTimeCount: z.int(),
        lateCount: z.int(),
        onTimeBp: z.int().nullable(),
        shortCount: z.int(),
        damagedCount: z.int(),
        openOrderCents: z.int(),
      }),
    ),
    /** Wareneingang je Woche bzw. Monat */
    series: z.array(z.object({ date: zDay, receivedCents: z.int() })),
    seriesBucket: z.enum(TIME_BUCKETS),
    csv: zCsv.optional(),
  }),
  permission: READ_PURCHASING,
  risk: 'read',
  scope: reportScope,
})

export const purchasingPrices = defineContract({
  name: 'reporting.purchasing.prices',
  title: 'Preisentwicklung im Einkauf anzeigen',
  description: 'Wie sich die Einkaufspreise der Lieferanten entwickelt haben: Änderungen im Zeitraum je Artikel mit altem und neuem Preis.',
  llm: {
    description:
      'Listet die Änderungen der hinterlegten Einkaufspreise, die im Zeitraum wirksam wurden oder noch werden (bis 60 Tage voraus), je Artikel und Lieferant: alter und neuer Preis in Cent netto je Bestelleinheit und die Veränderung in Basispunkten. ' +
      'Mit `itemId` der ganze Preisverlauf eines Artikels. Verlangt das Recht auf Einstandspreise. Verwenden für „Welche Lieferanten haben erhöht?“, „Wie hat sich der Einkaufspreis eines Artikels entwickelt?“. ' +
      'Nicht für Verkaufspreise (pricing.price.history).',
    keywords: ['Preiserhöhung', 'Einkaufspreis', 'Preisentwicklung', 'EK', 'teurer geworden'],
    examples: [{ period: 'last90' }],
  },
  input: z.strictObject({ ...periodFields, itemId: zId.optional(), supplierId: zId.optional(), limit: z.int().min(1).max(500).default(100), ...csvField }),
  output: z.object({
    period: zPeriod,
    totals: z.object({ changeCount: z.int(), increaseCount: z.int(), decreaseCount: z.int(), upcomingCount: z.int() }),
    rows: z.array(
      z.object({
        key: z.string(),
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        supplierName: z.string(),
        validFrom: zDay,
        /** true: wird erst wirksam */
        upcoming: z.boolean(),
        /** Preis je Bestelleinheit; die Einheit steht in `unit` */
        priceCents: z.int(),
        previousPriceCents: z.int().nullable(),
        changeBp: z.int().nullable(),
        unit: z.string(),
      }),
    ),
    truncated: z.boolean(),
    csv: zCsv.optional(),
  }),
  permission: READ_PURCHASING,
  risk: 'read',
  scope: 'any',
})

// ---- Pfand --------------------------------------------------------------------------------------------------------------

export const depositsReport = defineContract({
  name: 'reporting.deposits.report',
  title: 'Leergut im Umlauf auswerten',
  description: 'Leergut bei Kunden nach Pfandart und Kunde und die Pfandverbindlichkeit, die daraus entsteht.',
  llm: {
    description:
      'Wertet die Leergutkonten aus: je Pfandart die Stückzahl bei Kunden und der Pfandwert in Cent (das ist die Verbindlichkeit des Betriebs, wenn alles zurückkommt), die Kunden mit dem meisten Leergut und das Guthaben bei Lieferanten. ' +
      'Verwenden für „Wie viel Leergut ist draußen?“, „Wie hoch sind unsere Pfandverbindlichkeiten?“. Nicht für das Konto eines einzelnen Kunden (deposits.account.statement).',
    keywords: ['Leergut im Umlauf', 'Pfandverbindlichkeit', 'Leergutkonto', 'Pfandsaldo'],
  },
  input: z.strictObject({ limit: z.int().min(1).max(200).default(20), ...csvField }),
  output: z.object({
    asOf: zDay,
    totals: z.object({
      /** Pfandwert des Leerguts bei Kunden = Verbindlichkeit gegenüber Kunden */
      customerCents: z.int(),
      customerAccountCount: z.int(),
      /** Pfandwert des Leerguts, das der Betrieb Lieferanten schuldet bzw. bei ihnen gut hat (positiv = Guthaben des Betriebs) */
      supplierCents: z.int(),
      supplierAccountCount: z.int(),
    }),
    types: z.array(z.object({ depositTypeId: zId, name: z.string(), customerQty: z.int(), customerCents: z.int(), supplierQty: z.int(), supplierCents: z.int() })),
    customers: z.array(z.object({ partnerId: zId, number: z.string().nullable(), name: z.string(), balanceQty: z.int(), balanceCents: z.int(), shareBp: z.int() })),
    csv: zCsv.optional(),
  }),
  permission: READ_FINANCE,
  risk: 'read',
  scope: 'any',
  feature: 'deposits',
})

// ---- Finanzen ------------------------------------------------------------------------------------------------------------

export const financeReport = defineContract({
  name: 'reporting.finance.report',
  title: 'Forderungen und Zahlungseingang auswerten',
  description: 'Altersstruktur der offenen Forderungen, Forderungslaufzeit in Tagen und Umsatz gegen Zahlungseingang.',
  llm: {
    description:
      'Wertet die Forderungen aus: offene Posten der Kunden nach Alter seit Fälligkeit (nicht fällig, 1–30, 31–60, 61–90, über 90 Tage) in Cent, die Kunden mit den höchsten offenen Beträgen, ' +
      'die Forderungslaufzeit in Tagen (offene Forderungen ÷ berechneter Umsatz des Zeitraums × Tage) und je Woche bzw. Monat den berechneten Umsatz (Rechnungen brutto abzüglich Korrekturen) gegen den Zahlungseingang. ' +
      'Je Kunde ist `openCents` der Saldo aller offenen Posten (offene Gutschriften mindern ihn) und `overdueCents` die Summe der fälligen, unbezahlten Rechnungen – sie kann den Saldo deshalb übersteigen. ' +
      'Verwenden für „Wie alt sind unsere Außenstände?“, „Kommt das Geld so schnell herein, wie wir berechnen?“. Nicht für einzelne Posten (finance.openItem.list) oder den Mahnvorschlag (finance.dunning.propose).',
    keywords: ['Außenstände', 'Altersstruktur', 'überfällig', 'DSO', 'Zahlungseingang', 'Forderungen'],
    examples: [{ period: 'last30' }],
  },
  input: z.strictObject({ ...periodFields, ...locationFields, limit: z.int().min(1).max(200).default(20), ...csvField }),
  output: z.object({
    asOf: zDay,
    period: zPeriod,
    totals: z.object({
      receivablesCents: z.int(),
      overdueCents: z.int(),
      overdueCount: z.int(),
      /** berechneter Umsatz des Zeitraums brutto (Rechnungen abzüglich Korrekturen) */
      invoicedCents: z.int(),
      /** Zahlungseingang von Kunden im Zeitraum */
      receivedCents: z.int(),
      /** Forderungslaufzeit in Tagen; null ohne berechneten Umsatz */
      daysSalesOutstanding: z.int().nullable(),
    }),
    ageing: z.array(z.object({ bucket: z.enum(AGE_BUCKETS), label: z.string(), itemCount: z.int(), openCents: z.int(), shareBp: z.int() })),
    customers: z.array(
      z.object({ partnerId: zId, number: z.string().nullable(), name: z.string(), openCents: z.int(), overdueCents: z.int(), oldestDueDate: zDay.nullable(), daysOverdue: z.int() }),
    ),
    series: z.array(z.object({ date: zDay, invoicedCents: z.int(), receivedCents: z.int() })),
    seriesBucket: z.enum(TIME_BUCKETS),
    csv: zCsv.optional(),
  }),
  permission: READ_FINANCE,
  risk: 'read',
  scope: reportScope,
})
